import React, { useState } from 'react';
import { Button, Input, Modal, Typography, showToast } from '@attentive-platform/stem-ui';
import { InfoText } from '../../styled';
import { fetchApiData } from '../../Utils/ApiCalls';
import { RESET_DIY_COUNTERS } from '../../urls';
import { useTradeData } from '../../Hooks/useTradeData';
import {
  FormBox,
  ToggleRow,
  ToggleTrack,
  ToggleText,
  FieldRow,
  FieldBoxContainer,
  FieldLabel,
  FieldValue,
  SectionBlock,
  TradesRow,
  TradeChipButton
} from '../DIYEntitlement/styles';

const RESET_COUNTERS_TITLE = 'Reset DIY usage counters';

const FieldBox = ({ label, value, valueStyle, readOnly, children }) => (
  <FieldBoxContainer readonly={readOnly ? 1 : 0}>
    <FieldLabel>
      {label}
      {!readOnly && <span style={{ color: '#e53e3e', marginLeft: '2px' }}>*</span>}
    </FieldLabel>
    {children ?? <FieldValue style={valueStyle}>{value}</FieldValue>}
  </FieldBoxContainer>
);

const DIYEntitlementSection = ({
  form,
  setForm,
  diyRemaining,
  handleTradeToggle,
  email,
  licensePeriodResets,
  onAfterResetCounters
}) => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [reason, setReason] = useState('');
  const { tradesData } = useTradeData();

  const handleResetCounters = async () => {
    setShowResetConfirm(false);
    setResetLoading(true);
    try {
      const response = await fetchApiData({
        apiRequest: 'POST',
        endpoint: RESET_DIY_COUNTERS,
        data: { company_email: email, reason: reason || null }
      });
      if (response.error) {
        showToast(response.error || 'Failed to reset DIY usage counters', 'error');
        return;
      }
      const { diy_jobs_used } = response.data ?? {};
      setForm(prev => ({ ...prev, diyUsed: diy_jobs_used ?? 0 }));
      setReason('');
      showToast('DIY usage counters reset successfully', 'success');
      onAfterResetCounters?.();
    } catch {
      showToast('Failed to reset DIY usage counters', 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCancelReset = () => {
    setShowResetConfirm(false);
    setReason('');
  };

  return (
    <>
      <FormBox>
        <Typography variant='h6-medium' sx={{ marginBottom: '1rem' }}>
          DIY configuration
        </Typography>

        <ToggleRow>
          <ToggleTrack>
            <input
              type='checkbox'
              checked={form.diyEnabled}
              onChange={e => setForm(prev => ({ ...prev, diyEnabled: e.target.checked }))}
            />
            <span />
          </ToggleTrack>
          <ToggleText>
            <Typography variant='body3' style={{ fontWeight: 500 }}>
              DIY enabled
            </Typography>
            <Typography variant='body5' color='var(--color-neutral-60)' sx={{ lineHeight: 1.5 }}>
              When ON, users can create DIY takeoffs. When OFF, DIY job cap and AI trades are hidden.
            </Typography>
          </ToggleText>
        </ToggleRow>

        {form.diyEnabled && (
          <>
            <FieldRow>
              <FieldBox label='DIY Job Cap'>
                <Input
                  type='number'
                  min={0}
                  size='small'
                  value={form.diyJobCap}
                  onChange={e => setForm(prev => ({ ...prev, diyJobCap: e.target.value }))}
                  placeholder='e.g. 50'
                />
              </FieldBox>
              <FieldBox label='DIY Used' value={form.diyUsed} readOnly />
              <FieldBox label='DIY Remaining' value={diyRemaining} readOnly valueStyle={{ color: '#48bb78' }} />
            </FieldRow>
            {licensePeriodResets ? (
              <Typography variant='body5' color='var(--color-neutral-60)' sx={{ marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                Period resets: {licensePeriodResets}
              </Typography>
            ) : null}

            <SectionBlock>
              <Typography variant='h6-medium' sx={{ marginBottom: '1rem' }}>
                Allowed AI Trades
              </Typography>
              <InfoText>
                <Typography
                  variant='body5'
                  color='var(--color-neutral-60)'
                  style={{ marginBottom: '0.75rem', textAlign: 'left' }}>
                  Multi-select. Leave empty for Manual-only DIY. Manual mode is always enabled when DIY is on.
                </Typography>
              </InfoText>
              <TradesRow>
                {tradesData.map(trade => (
                  <TradeChipButton
                    key={trade.value}
                    type='button'
                    selected={form.allowedTrades.includes(trade.value) ? 1 : 0}
                    onClick={() => handleTradeToggle(trade.value)}>
                    {trade.label}
                  </TradeChipButton>
                ))}
              </TradesRow>
            </SectionBlock>

            <SectionBlock>
              <Typography variant='h6-medium' sx={{ marginBottom: '1rem' }}>
                {RESET_COUNTERS_TITLE}
              </Typography>
              <InfoText>
                <Typography variant='body5' color='var(--color-neutral-60)' sx={{ lineHeight: 1.5 }}>
                  Resets DIY used to 0 for the current period. Does not change DIY cap, AI trades, or DIY enabled state.
                  Requires explicit confirmation. Audit-logged.
                </Typography>
              </InfoText>
              <Button
                color='error'
                variant='outlined'
                loading={resetLoading}
                onClick={() => setShowResetConfirm(true)}>
                {RESET_COUNTERS_TITLE}
              </Button>
            </SectionBlock>
          </>
        )}
      </FormBox>

      <Modal
        show={showResetConfirm}
        onClose={handleCancelReset}
        heading={<span className='pl-4'>{RESET_COUNTERS_TITLE}</span>}
        content={
          <div className='flex flex-col gap-3 m-4' style={{ width: '420px' }}>
            <Typography variant='body3'>
              This will reset the DIY usage counter to 0 for the current period. This action is audit-logged and cannot
              be undone.
            </Typography>
            <div>
              <Typography variant='body5' color='var(--color-neutral-60)' style={{ marginBottom: '0.25rem', display: 'block' }}>
                Reason (optional)
              </Typography>
              <Input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder='e.g. Contract renewal, billing cycle correction...'
                size='large'
              />
            </div>
            <div className='flex justify-end gap-2 mt-2'>
              <Button variant='outlined' onClick={handleCancelReset}>
                Cancel
              </Button>
              <Button color='error' loading={resetLoading} onClick={handleResetCounters}>
                Reset
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default DIYEntitlementSection;
